import { useCallback, useMemo, type FC } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch } from "@ds/hooks";

import { PlaylistsAutoComplete } from "@ds/modules/content/playlists/components/playlists-autocomplete";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputNumber } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

import { campaignItemsActions } from "../../redux/slice";
import { createCampaignItemModel } from "../../utils/model";

const ContentWrapper = styled.div`
  min-height: 180px;
  width: 480px;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: right;
`;

type FormValues = { playlist: Playlist | null; weight: number };

export const CreateCampaignItemModal: FC<{
  campaign: Campaign;
  allItems: CampaignItem[];
  onClose: () => void;
}> = ({ campaign, allItems, onClose }) => {
  const isFirstItem = allItems.length === 0;

  const { control, watch, getValues, setValue, handleSubmit } = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: { playlist: null, weight: isFirstItem ? 100 : 10 },
  });

  const dispatch = useRootDispatch();
  const onSubmitHandler = useCallback(() => {
    const { playlist, weight } = getValues();
    if (!playlist) return;

    dispatch(
      campaignItemsActions.createCampaignItem(createCampaignItemModel({ playlist_id: playlist.id, weight }), {
        campaignId: campaign.id,
      }),
    );
    onClose();
  }, [dispatch, campaign.id, getValues, onClose]);

  const playlist = watch("playlist");
  const renderFooter = useMemo(
    () => (
      <FooterWrapper>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          onClick={onClose}
        />
        <Button
          label="Add"
          ariaLabel="add button"
          buttonType={ButtonType.Raised}
          icon={IconType.Create}
          iconPosition="right"
          isDisabled={!playlist}
          onClick={handleSubmit(onSubmitHandler)}
        />
      </FooterWrapper>
    ),
    [playlist, onClose, handleSubmit, onSubmitHandler],
  );

  return (
    <Dialog modal visible header="Add playlist to campaign" footer={renderFooter} onHide={onClose}>
      <ContentWrapper>
        <LabelWrapper label="Playlist" className="col-8" isRequired>
          <PlaylistsAutoComplete
            value={playlist ?? undefined}
            onChange={selected => setValue("playlist", selected as Playlist | null)}
            dropdownMode="current"
          />
        </LabelWrapper>

        <Controller
          name="weight"
          control={control}
          rules={{
            required: "Weight is required",
            min: { value: 1, message: "Weight must be at least 1" },
            max: { value: 100, message: "Weight must be at most 100" },
          }}
          render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
            <LabelWrapper label="Weight (%)" className="col-8" isRequired error={error}>
              <InputNumber
                inputRef={ref}
                value={Number(value)}
                min={1}
                max={100}
                isDisabled={isFirstItem}
                onChange={({ value: v }) => onChange(v ?? 1)}
              />
            </LabelWrapper>
          )}
        />
      </ContentWrapper>
    </Dialog>
  );
};
